/**
 * ArchivedTenantBanner — Phase 1.5 customer-side chrome.
 *
 * Per spec §Q3 lock 2026-05-12: top-of-page banner shown on every customer
 * surface when the caller's tenant is archived. Restore-friendly bias —
 * less aggressive than a hard-redirect, lets the user review existing
 * Blueprints + Profiles but can't change anything.
 *
 * Pairs with `requireActiveTenant` on the server side; if the customer
 * attempts a write while archived, the API returns 403 with
 * `code: "tenant_archived"` and the call site surfaces the existing
 * error UX. The banner is the steady-state visual cue.
 *
 * Stacks alongside `ImpersonationBanner` — both are sticky at the top.
 * When BOTH are present (the impersonator is reading an archived
 * customer tenant), the impersonation banner renders below the archive
 * banner to keep "what state am I in?" legible.
 */

import { useSession } from "../lib/useSession.js";

export default function ArchivedTenantBanner() {
  const { tenantArchived, tenantDisplayName } = useSession();
  if (!tenantArchived) return null;

  return (
    <div
      role="alert"
      data-testid="archived-tenant-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 998, // one below ImpersonationBanner's 999 so impersonation stays on top
        background: "var(--ppr-color-accent-navy)",
        color: "var(--ppr-color-background-page)",
        padding: "10px 24px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wide)",
        borderBottom: "2px solid var(--ppr-color-accent-coral)",
      }}
    >
      <span
        style={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          color: "var(--ppr-color-accent-coral)",
        }}
      >
        Tenant archived
      </span>
      <span style={{ flex: 1 }}>
        {tenantDisplayName ? <strong>{tenantDisplayName}</strong> : "This tenant"} is in <strong>read-only</strong> mode.
        Existing Blueprints and Profiles remain accessible; new writes are blocked.
        Contact support to restore.
      </span>
    </div>
  );
}
